import { addDays, differenceInDays, isAfter, isEqual, isWithinInterval } from 'date-fns';
import { ReactDatePickerProps } from 'react-datepicker';
import { ShiftDate } from '../../../../types';
import { Side, DateCell } from '../../types';
import { MAXIMUM_NIGHTS } from './constants';
import { CalendarProps } from './types';

export const isInInterval = ({ date, pickerDate, shift }: DateWithShiftCompare): boolean => {
  if (!pickerDate) return false;

  return isWithinInterval(date, {
    start: addDays(pickerDate, -shift.left),
    end: addDays(pickerDate, shift.right),
  });
};

export const isEqualWithShift = ({
  date,
  pickerDate,
  shift,
  side,
}: DateWithShiftCompare & SideShift): boolean => {
  if (!pickerDate) return false;

  const additionalDays = side === 'right' ? shift[side] : -shift[side];
  return isEqual(date, addDays(pickerDate, additionalDays));
};

export const isEqualWithMaxShift = (params: DateWithShiftCompare & SideShift): boolean =>
  isEqualWithShift(params) && params.shift[params.side] < 2;

export const isAfterWithShift = ({
  pickerDate,
  shift,
  minDate = new Date(),
}: Omit<DateWithShiftCompare, 'date'> & { minDate?: Date }) => {
  if (!pickerDate) return false;

  return isAfter(addDays(pickerDate, -shift.left), minDate);
};

export const getCornerDates = ({
  date,
  shift: { left, right },
}: Pick<DateCell, 'shift'> & { date: Date | string | null }): {
  from: Date;
  to: Date;
} => {
  const formatDate = date instanceof Date ? date : new Date(date || 0);
  return {
    from: addDays(formatDate, -left),
    to: addDays(formatDate, right),
  };
};

export const isMaxRangeDates = ({
  startDate,
  endDate,
  endShift,
}: Pick<CalendarProps, 'startDate' | 'endDate' | 'endShift'>): boolean => {
  if (!startDate || !endDate) return false;

  return isEqual(addDays(startDate, MAXIMUM_NIGHTS), addDays(endDate, endShift.right));
};

export const getRangeNightsString = ({
  startDate,
  startShift,
  endDate,
  endShift,
}: Pick<CalendarProps, 'endShift' | 'startShift'> & {
  startDate: Date;
  endDate: Date;
}): { maxNights: number; minNights?: number } => {
  const minNights = differenceInDays(
    addDays(endDate, -endShift.left),
    addDays(startDate, startShift.right)
  );
  const maxNights = differenceInDays(
    addDays(endDate, endShift.right),
    addDays(startDate, -startShift.left)
  );

  return {
    maxNights,
    minNights: minNights === maxNights ? undefined : minNights,
  };
};

type SideShift = {
  side: Side;
};

type DateWithShiftCompare = {
  date: Date;
  pickerDate: ReactDatePickerProps['startDate' | 'endDate'];
  shift: ShiftDate;
};
